import shopQueries from '@apis/queries/shopQueries';
import AuthGuard, { AuthFlow } from '@components/AuthGuard';
import Button from '@components/Button';
import Row from '@components/Row';
import { css } from '@emotion/react';
import { ItemResponse } from '@models/item';
import { Link } from '@stackflow/link';
import { useFlow } from '@stackflow/react';
import { useItemActivityId } from '@store/item/context/ItemActivityContext';
import useCalculatedOrder from '@store/item/hooks/useCalculatedOrder';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { useQuery } from '@tanstack/react-query';
import { orderLineToGtmFormat, pushEcommerceEvent } from '@utils/gtm';
import { useTranslation } from 'react-i18next';

import useAddCart from './hooks/useAddCart';
import OptionSelectionGuard from './OptionSelectionGuard';

interface Props {
    shopId: string;
    isSoldOut: boolean;
    item: ItemResponse;
}

function OrderRow({ shopId, isSoldOut, item }:Props) {
    const { t } = useTranslation('item');
    const itemId = item.id;
    const activityId = useItemActivityId();
    const { push } = useFlow();
    const { handleAddToCart } = useAddCart(shopId);
    const authFlows: AuthFlow[] = [
        { activityName: 'ShopListActivity', activityParams: { isOrder: false, type: 'LIST' } },
        { activityName: 'ItemsActivity', activityParams: { shopId, type: 'ALL' } },
        { activityName: 'ItemActivity', activityParams: { itemId, shopId } },
    ];
    const { data: shop } = useQuery(shopQueries.detail(shopId));
    const { orderLines } = useCalculatedOrder();
    const handleSendSelectEvent = () => {
        pushEcommerceEvent('select_item', {
            items: [orderLineToGtmFormat({ orderLine: orderLines[0], shop })],
        });
        push('OrderActivity', { path: 'direct', itemActivityId: activityId });
    };
    return (
        <Row spacing={4} >
            <Row fullWidth spacing={2}>
                <AuthGuard flows={authFlows}>
                    <OptionSelectionGuard item={item}>
                        <Button fullWidth variant='tone'
                            id='item-add-to-card-button'
                            onClick={handleAddToCart}
                            noPadding>
                            {t('footer.addToCart')}
                        </Button>
                    </OptionSelectionGuard>
                </AuthGuard>
                <AuthGuard flows={authFlows}>
                    <OptionSelectionGuard item={item}>
                        <Button fullWidth
                            id='order-now-button'
                            onClick={handleSendSelectEvent}
                            disabled={isSoldOut}
                            noPadding>
                            {isSoldOut ? t('footer.soldOut') : t('footer.orderNow')}
                        </Button>
                    </OptionSelectionGuard>
                </AuthGuard>
            </Row>
        </Row>
    );
}

export default OrderRow;
